import type {Size2D} from '../../../types/Size2D';
import {Rectangle, Point} from 'pixi.js';
import {ScreenOrientation} from '@gongxigames/framework/core/types';

export interface TranslateConfig {
    /**
     * The scale value that should be used to fit in [[SafeZoneOptions.frame]]
     */
    scale: number;

    /**
     * The position value that should be used to be in center of [[SafeZoneOptions.frame]]
     */
    position: Point;
};

export type SafeZoneOptions = {
    /**
     * The frame in which the container should fit
     */
    frame: Rectangle;

    /**
     * Size of the container that we wont to auto scale.
     */
    contentSize: Size2D;

    /**
     * Hols information about current orientation.
     */
    orientation: ScreenOrientation;

    /**
     * Screen size for helper functions.
     */
    screenSize: Size2D;
};

export class SafeZone implements TranslateConfig {
    public readonly scale: number;
    public readonly position: Point;
    public readonly isPortrait: boolean;

    constructor(
        public readonly options: SafeZoneOptions
    ){
        const {frame, contentSize} = options;

        this.scale = Math.min(frame.width / contentSize.width , frame.height / contentSize.height);

        const scale = this.scale;
        const newWidth = contentSize.width * scale;
        const newHeight = contentSize.height * scale;

        const offsetX = (frame.width / 2 + frame.x) - (newWidth / 2);
        const offsetY = (frame.height / 2 + frame.y) - (newHeight / 2);

        this.position = new Point(offsetX, offsetY);

        this.isPortrait = options.orientation === ScreenOrientation.PORTRAIT;
    }

    /**
     * For full screen fader rectangle calculation.
     */
    calcInSafeFader(result = new Rectangle()): Rectangle {
        const {screenSize} = this.options;
        const {scale} = this;

        result.width = screenSize.width / scale;
        result.height = screenSize.height / scale;

        result.x = -this.position.x / scale;
        result.y = -this.position.y / scale;

        return result;
    }

    /**
     * Calc full screen background image props
     */
    calcInSafeFullScreenCoverBg(imgSize: Size2D, result?: TranslateConfig, align?: Point): TranslateConfig {
        const {scale, options} = this;
        const {screenSize} = options;
        const newScale = Math.max(screenSize.width / imgSize.width, screenSize.height / imgSize.height) / scale;

        const offsetX = -this.position.x / scale;
        const offsetY = -this.position.y / scale;

        const newWidth = imgSize.width * newScale;
        const newHeight = imgSize.height * newScale;

        const alignX = align?.x ?? 0.5;
        const alignY = align?.y ?? 0.5;

        const x = offsetX + ((screenSize.width / scale) - newWidth) * alignX;
        const y = offsetY + ((screenSize.height / scale) - newHeight) * alignY;

        if (!result) {
            return {
                position: new Point(x, y),
                scale: newScale
            };
        }

        result.position.set(x, y);
        result.scale = newScale;

        return result;
    }

    calcInSafeFullScreenFit(size: Size2D, result?: TranslateConfig, align?: Point): TranslateConfig {
        const {scale, options} = this;
        const {screenSize} = options;
        const newScale = Math.min(screenSize.width / size.width, screenSize.height / size.height) / scale;

        const offsetX = -this.position.x / scale;
        const offsetY = -this.position.y / scale;

        const newWidth = size.width * newScale;
        const newHeight = size.height * newScale;

        const alignX = align?.x ?? 0.5;
        const alignY = align?.y ?? 0.5;

        const x = offsetX + ((screenSize.width / scale) - newWidth) * alignX;
        const y = offsetY + ((screenSize.height / scale) - newHeight) * alignY;

        if (!result) {
            return {
                position: new Point(x, y),
                scale: newScale
            };
        }

        result.position.set(x, y);
        result.scale = newScale;

        return result;
    }

    calcScreenCenter(result = new Point()): Point {
        const {screenSize} = this.options;
        const {scale} = this;

        const x = screenSize.width / scale / 2;
        const y = screenSize.height / scale / 2;

        result.x = -this.position.x / scale + x;
        result.y = -this.position.y / scale + y;

        return result;
    }

    static default(): SafeZone {
        // any non zero values
        return new SafeZone({
            contentSize: {width: 1024, height: 1024},
            frame: new Rectangle(0, 0, 512, 512),
            orientation: ScreenOrientation.PORTRAIT,
            screenSize: {width: 512, height: 512}
        });
    }
}